/**
 * WritebackReviewCard — "here's what i noted" (PPL-335, UI §W3).
 *
 * A pointer, not a decision surface: the card says how much is waiting and
 * sends the user to `/insights`, where accept/edit/dismiss actually live.
 * Deliberately read-only — deciding on an item needs its provenance, its
 * person and (for unassigned items) a roster picker, none of which fit under
 * a chat reply.
 *
 * It carries **no extracted content**: per-item lines would put "her father
 * died" into a chat transcript the user did not ask to re-read, in a surface
 * that may well be on screen in company. Type and person only.
 *
 * PPL-451: every chip label is **server-authored** — the client composes no
 * copy of its own. That is what keeps the privacy rule enforceable in one
 * place: a calendar attendee is described by the event it came from and never
 * by the attendee's address, and `data.groups` arrives already pluralised
 * ("3 people from your calendar"), so the client never counts, never
 * pluralises and never re-prefixes a person's name onto a label. The old
 * `personName · type` formatting survives only as the last-resort branch for
 * payloads built before this change.
 *
 * PPL-454: `data.saved` is an ADDITIVE roll-up of what f8 saved by itself —
 * counts and first names only, never what was saved, because the content-free
 * rule is the whole reason this card is safe in a transcript. It is
 * deliberately NOT a `groups` entry: groups render INSTEAD of per-item chips,
 * so a saved group would hide every pending chip on builds already shipped.
 * A build that does not know the key renders exactly what it renders today.
 *
 * The server only ever sends `data.saved` ALONGSIDE pending items: a
 * saved-only roll-up rides as plain text in f8's reply instead, because this
 * card's body copy lives here on the client and every shipped build renders
 * it as a pending-queue card. The `count === 0` branches below are therefore
 * defensive, not a shape to design for.
 */

import React from 'react';
import { ChevronRight } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  type F8CardData,
  type OnAction,
} from './cardHelpers';

interface ReviewItem {
  suggestionId: string;
  type: string;
  personName: string | null;
  /** Server-authored, complete (already carries the person's name). */
  label?: string | null;
}

/** A per-type roll-up of the whole queue; `label` is already pluralised. */
interface ReviewGroup {
  kind: string;
  label: string;
  count: number;
}

/** What f8 saved on its own, recently. Counts and first names only. */
interface SavedRollup {
  count: number;
  people: string[];
}

/** `person_ambiguous` → "who", `log_interaction` → "log interaction". */
function typeLabel(type: string): string {
  if (!type) return 'note';
  if (type.startsWith('person_') || type === 'new_person') return 'who';
  return type.replace(/_/g, ' ');
}

export default function WritebackReviewCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const data = card?.data || {};
  const count: number = typeof data.count === 'number' ? data.count : 0;
  const items: ReviewItem[] = Array.isArray(data.items) ? data.items : [];
  const groups: ReviewGroup[] = Array.isArray(data.groups) ? data.groups : [];

  // Read defensively: this key arrives from a newer server than some builds
  // that render this card, and vice versa.
  const savedRaw = data.saved as Partial<SavedRollup> | undefined;
  const savedCount = typeof savedRaw?.count === 'number' ? savedRaw.count : 0;
  const savedPeople: string[] = Array.isArray(savedRaw?.people)
    ? (savedRaw?.people as unknown[]).filter((n): n is string => typeof n === 'string' && !!n.trim())
    : [];

  // Three layers, in order: server groups → server per-item label → the
  // pre-PPL-451 formatting, for a payload built by an older server.
  const usableGroups = groups.filter(
    (g) => g && typeof g.label === 'string' && g.label.trim(),
  );
  const pendingChips: Array<{ key: string; text: string; saved?: boolean }> = usableGroups.length
    ? usableGroups.map((g) => ({ key: `g:${g.kind}`, text: g.label }))
    : items.map((i) => ({
        key: i.suggestionId,
        text:
          i.label ||
          `${i.personName ? `${i.personName} · ` : ''}${typeLabel(i.type)}`,
      }));

  // One chip for everything f8 saved itself, in front of what is still
  // waiting. Named only when the whole roll-up is about one person —
  // "saved to john and 2 others" would be counting people the card was not
  // given.
  const chips = savedCount
    ? [{
      key: 'saved',
      saved: true,
      text: savedPeople.length === 1
        ? `saved to ${savedPeople[0]} · ${savedCount}`
        : `saved · ${savedCount}`,
    }, ...pendingChips]
    : pendingChips;

  return (
    <InlineShell>
      <InlineEyebrow label="what i noted" trailing={count > 0 ? `${count} waiting` : undefined} />
      <div style={{ fontSize: 12.5, color: 'rgba(232,220,250,0.7)', lineHeight: 1.5 }}>
        f8 saves the ones it&apos;s sure of — the rest wait for you.
      </div>

      {chips.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {chips.map((c) => (
            <span
              key={c.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: c.saved ? 'rgba(52,211,153,0.1)' : 'rgba(217,70,239,0.1)',
                border: `1px solid ${c.saved ? 'rgba(52,211,153,0.26)' : 'rgba(217,70,239,0.24)'}`,
                fontSize: 11.5,
                color: 'rgba(240,232,250,0.85)',
                maxWidth: '100%',
              }}
            >
              {c.text}
            </span>
          ))}
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        <CardButton
          primary
          small
          onClick={() => {
            void onAction?.({ action: 'navigate', params: { screen: 'Insights' } });
          }}
        >
          {count > 0 ? `review ${count > 1 ? 'them' : 'it'}` : 'open insights'} <ChevronRight size={12} />
        </CardButton>
      </div>
    </InlineShell>
  );
}
